import { FormEvent, useCallback, useEffect, useState } from 'react'
import { toORPCError } from '@orpc/client'
import { useTranslation } from 'react-i18next'
import { useDateLocale } from '@/i18n/dates'
import { orpc } from '@/lib/orpc-client'
import { useProject } from '@/lib/project-context'
import { useAuthState } from '@/lib/auth-state'
import { Button } from '@/components/ui/button'
import { SettingsPage, SettingsSection } from '@/components/settings/settings-layout'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'

type MembersData = Awaited<ReturnType<typeof orpc.members.list>>
type ProjectMember = MembersData['members'][number]
type ProjectInvite = MembersData['invites'][number]

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((p) => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return email[0]?.toUpperCase() ?? '?'
}

export function MembersPage({ projectId }: { projectId: string }) {
  const { project } = useProject()
  const { me } = useAuthState()
  const { t } = useTranslation('settings')
  const { intl } = useDateLocale()
  const roleLabel = (role: string) =>
    role === 'owner' || role === 'admin' || role === 'member' ? t(`role.${role}`) : role
  const currentUserId = me?.user.id
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [invites, setInvites] = useState<ProjectInvite[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [isInviting, setIsInviting] = useState(false)
  const [inviteNotice, setInviteNotice] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const callerRole = project.role

  const fetchMembers = useCallback(async () => {
    try {
      const data = await orpc.members.list({ projectId })
      setMembers(data.members)
      setInvites(data.invites)
      setError(null)
    } catch {
      setError(t('members.loadFailed'))
    } finally {
      setIsLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault()
    setInviteError(null)
    setInviteNotice(null)
    setIsInviting(true)

    try {
      const result = await orpc.members.invite({
        projectId,
        email: inviteEmail,
        role: inviteRole,
      })

      if (result.status === 'added') {
        setInviteNotice(t('members.added'))
      } else {
        setInviteNotice(t('members.inviteSent'))
      }
      setInviteEmail('')
      setInviteRole('member')
      await fetchMembers()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setInviteError(orpcErr.message ?? t('members.inviteFailed'))
    } finally {
      setIsInviting(false)
    }
  }

  const handleRemove = async (userId: string) => {
    try {
      await orpc.members.remove({ projectId, userId })
      await fetchMembers()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('members.removeFailed'))
    }
  }

  const handleRevoke = async (inviteId: string) => {
    try {
      await orpc.members.revokeInvite({ projectId, inviteId })
      await fetchMembers()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('members.revokeFailed'))
    }
  }

  const handleToggleDisabled = async (userId: string, disabled: boolean) => {
    try {
      await orpc.members.toggleDisabled({ projectId, userId, disabled })
      await fetchMembers()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('members.statusFailed'))
    }
  }

  const handleChangeRole = async (userId: string, role: 'admin' | 'member') => {
    try {
      await orpc.members.changeRole({ projectId, userId, role })
      await fetchMembers()
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('members.roleFailed'))
    }
  }

  const handleResendInvite = async (inviteId: string) => {
    try {
      await orpc.members.resendInvite({ projectId, inviteId })
      setError(null)
      setInviteNotice(t('members.resent'))
    } catch (err) {
      const orpcErr = toORPCError(err)
      setError(orpcErr.message ?? t('members.resendFailed'))
    }
  }

  const canManage = callerRole === 'owner' || callerRole === 'admin'

  if (isLoading) {
    return (
      <SettingsPage>
        <p className="py-6 text-sm text-muted-foreground">{t('members.loading')}</p>
      </SettingsPage>
    )
  }

  return (
    <SettingsPage>
      {canManage && (
        <SettingsSection title={t('members.inviteTitle')}>
          <div>
            <form onSubmit={handleInvite} className="flex flex-wrap items-end gap-3">
              <label className="min-w-56 flex-1 space-y-1.5">
                <span className="text-sm font-medium text-foreground">
                  {t('members.emailLabel')}
                </span>
                <input
                  type="email"
                  required
                  className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                  placeholder={t('members.emailPlaceholder')}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </label>
              <label className="w-28 space-y-1.5">
                <span className="text-sm font-medium text-foreground">
                  {t('members.roleLabel')}
                </span>
                <select
                  className="h-10 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                >
                  <option value="member">{t('role.member')}</option>
                  <option value="admin">{t('role.admin')}</option>
                </select>
              </label>
              <Button type="submit" disabled={isInviting}>
                {isInviting ? t('members.inviting') : t('members.invite')}
              </Button>
            </form>
            {inviteNotice && <p className="mt-3 text-sm text-muted-foreground">{inviteNotice}</p>}
            {inviteError && <p className="mt-3 text-sm text-destructive">{inviteError}</p>}
          </div>
        </SettingsSection>
      )}

      <SettingsSection title={t('members.title')}>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-col divide-y divide-border/60">
          {members.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('members.empty')}</p>
          )}
          {members.map((m) => {
            const isSelf = m.userId === currentUserId
            const canEditRole = canManage && m.role !== 'owner' && !isSelf

            return (
              <div
                key={m.userId}
                className={`flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0${m.disabledAt ? ' opacity-50' : ''}`}
              >
                <Avatar className="size-9">
                  {m.avatarUrl && <AvatarImage src={m.avatarUrl} alt={m.name ?? m.email} />}
                  <AvatarFallback>{getInitials(m.name, m.email)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {m.name ?? m.email}
                    {isSelf && (
                      <span className="ml-1 text-xs text-muted-foreground">{t('members.you')}</span>
                    )}
                  </p>
                  {m.name && <p className="truncate text-xs text-muted-foreground">{m.email}</p>}
                </div>
                {m.disabledAt && (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                    {t('members.disabled')}
                  </Badge>
                )}
                {m.isOrgGuest && (
                  <Badge variant="outline" className="text-xs">
                    {t('members.guest')}
                  </Badge>
                )}
                {canEditRole ? (
                  <select
                    className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                    value={m.role}
                    onChange={(e) =>
                      handleChangeRole(m.userId, e.target.value as 'admin' | 'member')
                    }
                  >
                    <option value="member">{t('role.member')}</option>
                    <option value="admin">{t('role.admin')}</option>
                  </select>
                ) : (
                  <Badge variant={m.role === 'owner' ? 'default' : 'secondary'}>
                    {roleLabel(m.role)}
                  </Badge>
                )}
                {canManage && m.role !== 'owner' && !isSelf && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleToggleDisabled(m.userId, !m.disabledAt)}
                    >
                      {m.disabledAt ? t('members.enable') : t('members.disable')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleRemove(m.userId)}>
                      {t('members.remove')}
                    </Button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </SettingsSection>

      {invites.length > 0 && (
        <SettingsSection title={t('members.pendingInvites')}>
          <div className="flex flex-col divide-y divide-border/60">
            {invites.map((inv) => (
              <div
                key={inv.id}
                className="flex flex-wrap items-center gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <Avatar className="size-9">
                  <AvatarFallback>{inv.email[0]?.toUpperCase() ?? '?'}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{inv.email}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('members.invitedOn', {
                      date: new Date(inv.createdAt).toLocaleDateString(intl),
                    })}
                  </p>
                </div>
                <Badge variant="secondary">{roleLabel(inv.role)}</Badge>
                {canManage && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => handleResendInvite(inv.id)}>
                      {t('members.resend')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleRevoke(inv.id)}>
                      {t('members.revoke')}
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </SettingsSection>
      )}
    </SettingsPage>
  )
}
